
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminOpportunities from "@/components/admin/AdminOpportunities";
import AdminClubs from "@/components/admin/AdminClubs";
import AdminMentors from "@/components/admin/mentors/AdminMentors";
import AdminJobs from "@/components/admin/AdminJobs";
import AdminStartups from "@/components/admin/AdminStartups";
import ManualPaymentRequests from "@/components/admin/ManualPaymentRequests";
import SubscriptionManagement from "@/components/admin/SubscriptionManagement";
import FeedbackManagement from "@/components/admin/FeedbackManagement";
import TeenOfTheDayManagement from "@/components/admin/TeenOfTheDayManagement";
import { Loader2, Shield, Users, MessageSquare, Star, CreditCard, UserPlus, Building, Briefcase, Trophy } from "lucide-react";

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    async function checkAdminStatus() {
      try {
        setLoading(true);
        
        // Special check for hassaanabegam@gmail.com
        if (user.email === "hassaanabegam@gmail.com") {
          setIsAdmin(true);
          setLoading(false);
          return;
        }
        
        // Check if user has admin role in database
        const { data, error } = await supabase
          .from("user_roles")
          .select("*")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .single();

        if (error && error.code !== "PGRST116") {
          throw error;
        }

        setIsAdmin(!!data);
        
        if (!data) {
          // If not admin, redirect to dashboard
          navigate("/dashboard");
        }
      } catch (error: any) {
        console.error("Error checking admin status:", error.message);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    }

    checkAdminStatus();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#FF8C00] mx-auto mb-4" />
          <p className="text-gray-600">Checking admin permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50/80 via-blue-50/60 to-indigo-50/70">
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-[#FF8C00]" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[#FF8C00] to-orange-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Welcome, Hassaana! Manage all platform features and user requests from here.
          </p>
        </div>
        
        <Tabs defaultValue="feedback" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto flex flex-nowrap mb-6 bg-white/80 backdrop-blur-sm">
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Feedback & Support
            </TabsTrigger>
            <TabsTrigger value="teen-of-day" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Teen of the Day
            </TabsTrigger>
            <TabsTrigger value="featuring" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Featuring Requests
            </TabsTrigger>
            <TabsTrigger value="premium" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Premium Requests
            </TabsTrigger>
            <TabsTrigger value="mentors" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Mentor Applications
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Team Applications
            </TabsTrigger>
            <TabsTrigger value="startups" className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              Startup Accounts
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Opportunities
            </TabsTrigger>
            <TabsTrigger value="clubs">Clubs</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
          </TabsList>
          
          <TabsContent value="feedback" className="space-y-6">
            <FeedbackManagement />
          </TabsContent>
          
          <TabsContent value="teen-of-day" className="space-y-6">
            <TeenOfTheDayManagement />
          </TabsContent>
          
          <TabsContent value="featuring" className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200/60 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Star className="h-6 w-6 text-yellow-500" />
                <h2 className="text-2xl font-bold">Featuring Requests</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Manage requests from startups and founders to be featured on the platform
              </p>
              <div className="text-center py-12 text-muted-foreground">
                <Star className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No featuring requests at the moment</p>
                <p className="text-sm">New requests will appear here for your review</p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="premium" className="space-y-6">
            <SubscriptionManagement />
          </TabsContent>
          
          <TabsContent value="mentors" className="space-y-6">
            <AdminMentors />
          </TabsContent>
          
          <TabsContent value="team" className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200/60 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <UserPlus className="h-6 w-6 text-blue-500" />
                <h2 className="text-2xl font-bold">Team Join Requests</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Review applications from users wanting to join the TeenVisor team
              </p>
              <div className="text-center py-12 text-muted-foreground">
                <UserPlus className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No team applications at the moment</p>
                <p className="text-sm">New applications will appear here for your review</p>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="startups" className="space-y-6">
            <AdminStartups />
          </TabsContent>
          
          <TabsContent value="opportunities" className="space-y-6">
            <AdminOpportunities />
          </TabsContent>
          
          <TabsContent value="clubs" className="space-y-6">
            <AdminClubs />
          </TabsContent>
          
          <TabsContent value="jobs" className="space-y-6">
            <AdminJobs />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
